// shape / basicShape — decorative rectangle/rounded rect/ellipse.

import type { VisualProps } from "./types";
import { palette } from "../theme";
import "./_static.css";

// AGENT-C: parse "#rrggbb" → {r,g,b}. Tolerant of "#rgb" too.
function _hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  if (!hex) return null;
  let s = hex.trim();
  if (s.startsWith("#")) s = s.slice(1);
  if (s.length === 3) {
    s = s
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (s.length !== 6) return null;
  const n = parseInt(s, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

// AGENT-C: clamp [0,255], integer-coerce.
function _clamp255(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 255) return 255;
  return Math.round(n);
}

function _rgbToHex(r: number, g: number, b: number): string {
  const h = (n: number) => _clamp255(n).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

// AGENT-C: apply a PBI-style lightness modifier in percent units.
//   - `pct > 0` blends toward white by `pct/100`.
//   - `pct < 0` blends toward black by `|pct|/100`.
//   - `pct === 0` leaves the base color untouched.
function _adjustLightness(hex: string, pct: number): string {
  const rgb = _hexToRgb(hex);
  if (!rgb) return hex;
  const p = Math.max(-100, Math.min(100, pct)) / 100;
  if (p === 0) return hex;
  const target = p > 0 ? 255 : 0;
  const t = Math.abs(p);
  return _rgbToHex(
    rgb.r + (target - rgb.r) * t,
    rgb.g + (target - rgb.g) * t,
    rgb.b + (target - rgb.b) * t,
  );
}

// AGENT-C: resolve one of the strings produced by the spec extractor.
//   - Literal hex / css color → returned as-is.
//   - "theme:<colorId>:<percent>" → palette[colorId % palette.length] adjusted
//     by `percent` lightness.
//   - Anything unparseable → null so the caller can fall back to a default.
export function resolveThemeColor(s: string | null | undefined): string | null {
  if (!s) return null;
  const trimmed = s.trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith("theme:")) {
    // Literal color (hex, rgb, named) — trust the extractor.
    return trimmed;
  }
  const parts = trimmed.split(":");
  if (parts.length < 2) return null;
  const idx = Number(parts[1]);
  if (!Number.isFinite(idx)) return null;
  const pct = parts.length >= 3 ? Number(parts[2]) : 0;
  const base = palette[((idx % palette.length) + palette.length) % palette.length];
  return _adjustLightness(base, Number.isFinite(pct) ? pct : 0);
}

// AGENT-C: ShapeInfo currently types only `shape` + `fill`. The patcher
// (_patch_pages_visuals.py) also writes a `stroke` sibling — read it with a
// narrow cast so the file remains type-safe without touching the shared
// types module.
interface _ShapeInfoWithStroke {
  shape?: string;
  fill?: string | null;
  stroke?: string | null;
}

export function ShapeVisual({ spec }: VisualProps) {
  const info = (spec.shapeInfo ?? {}) as _ShapeInfoWithStroke;
  const kind = info.shape ?? "rectangle";

  // AGENT-C: resolve fill + stroke through the theme palette. Null/empty
  // strings retain the original sensible defaults (transparent fill, soft
  // border) so untyped / older specs still render cleanly.
  const resolvedFill = resolveThemeColor(info.fill ?? null);
  const resolvedStroke = resolveThemeColor(info.stroke ?? null);
  const fill = resolvedFill ?? "transparent";
  const stroke = resolvedStroke ?? "#edebe9";

  const radius =
    kind === "rectangleRounded" ? 8 : kind.startsWith("ellipse") ? 9999 : 0;

  return (
    <div
      className="static-shape"
      style={{
        background: fill,
        borderRadius: radius,
        // AGENT-C: stroke maps to a 1px solid border in the resolved color.
        border: `1px solid ${stroke}`,
      }}
    />
  );
}
